import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ProcedureStatusInfo from '../../MoveAction/RevisionPreview/ProcedureStatusInfo';
import PreviewContent from '../../components/PreviewContent';
import Loader from '../../../../../common/Loader';
import AppBar from '../../../../../common/AppBar';
import ShowLightText from '../../../../../common/SmallButUseFull/ShowLightText';
import { convertUtcToFormattedTime } from '../../../../../util/AppUtils';
import { getProcedureRevisionById } from '../../../../../redux/procedureRevisionSlice';
import { useAuditTrailPreview } from '../../hooks/useAuditTrailPreview';

const CreateRevisionPreview = () => {
  const { audit_trail_id } = useParams()
  const dispatch = useDispatch()
  const { loading } = useSelector(state => state.audittrail)


  // Extract revisionNodes from version_content
  // revisionNodesSnapshot is an S3 URL pointing to a JSON file; fetch it and return the parsed data.
  // Falls back to the legacy revisionNodes field for older audit trail records.
  const getDataFromVersionContent = useCallback(async (version_content,new_content_snapshot) => {
    const snapshotUrl = version_content.status_changes_preview ? new_content_snapshot : version_content?.revisionNodesSnapshot;
    if (snapshotUrl) {
      try {
        const response = await fetch(snapshotUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch revisionNodesSnapshot: ${response.statusText}`);
        }
        const data = await response.json();

          return data;
      } catch (error) {
        console.error("Error fetching revisionNodesSnapshot:", error);
        return null;
      }
    }
    // Fallback for older records that still have revisionNodes inline
    return version_content?.revisionNodes || null;
  }, []);

  const { audit_trail_data, contentData } = useAuditTrailPreview(
    audit_trail_id,
    getDataFromVersionContent
  );

  const procedureRevisionId =
    audit_trail_data?.procedure_revision?.procedure_revision_id ??
    audit_trail_data?.version_content?.procedure_revision_id ??
    null

  let revisionRow = audit_trail_data?.version_content


  let basedOnText = null
  let currentText = null
  if (revisionRow) {
    const status = revisionRow.status_changes?.find((s) => s.is_latest)
    const user = status?.audit_trail?.user
    if (revisionRow.based_on_version_details?.version_content) {
      const { rev, current_status, procedure_number } =
        revisionRow.based_on_version_details.version_content
      basedOnText = (
        <>
          Based on {procedure_number} Rev {rev}, When {current_status}, at{" "}
          {convertUtcToFormattedTime(
            revisionRow.based_on_version_details.created_at,
          )}
        </>
      )
    }
    if (status) {
      const nameSuffix = [user?.first_name, user?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim()
      currentText = `Last ${
        status.status_changed_to === "Not Submitted"
          ? "Created"
          : status.status_changed_to
      } on ${convertUtcToFormattedTime(status.created_at)}${
        nameSuffix ? ` ${nameSuffix}` : ""
      }`
    }
  }

  return (
    <div style={{ paddingBottom: "10px" }}>
      <AppBar
        title={`${audit_trail_data.version_content?.type} ${audit_trail_data.version_content?.procedure_number}, Rev ${audit_trail_data.version_content?.to_part_rev || audit_trail_data.version_content?.rev}`}
        showBlackTitle={true}
        showBackButton={true}
        showCancelButton={false}
        showTablesDropdown={false}
        showProfileButton={false}
      />

      {loading ? <Loader /> : (
        <>
          {(basedOnText || currentText) && (
            <div
              style={{
                fontSize: "12px",
                color: "#444",
                margin: "0 20px 8px",
              }}
            >
              {basedOnText && <ShowLightText text={basedOnText} />}
              {currentText && <ShowLightText text={currentText} />}
            </div>
          )}
          <ProcedureStatusInfo
            item_code_type={audit_trail_data.version_content?.type || audit_trail_data.version_content?.item_code_type}
            partNumber={audit_trail_data.version_content?.procedure_number || audit_trail_data.version_content?.partNumber}
            status={audit_trail_data.version_content?.status || audit_trail_data.version_content?.status_changed_to || "Draft"}
            revision={audit_trail_data.version_content?.to_part_rev || audit_trail_data.version_content?.rev}
            partDescription={audit_trail_data.version_content?.description || audit_trail_data.version_content?.partDescription}
          />

          <PreviewContent contentData={contentData} />
        </>
      )}
    </div>
  )
}

export default CreateRevisionPreview
